import { useState } from 'react';
import { Link } from 'react-router-dom';
import { parseMoneyInput } from '@/lib/subscriptionPlans';
import type { Besoin, BesoinProposition } from '@/lib/besoins';

interface Props {
  besoin: Besoin;
  propositions: BesoinProposition[];
  onClose: () => void;
  onAccept: (proposition: BesoinProposition) => Promise<void>;
  onAnnoncer: () => Promise<void>;
  onCloturer: () => Promise<void>;
  /** Le demandeur révise son prix après avoir vu les offres. */
  onUpdateBudget: (budget: number | null, monaie: string) => Promise<void>;
  /** Page de messagerie ouverte depuis une mission attribuée. */
  contactHref?: string;
}

function fmt(value: number | null, devise: string): string {
  const n = Number(value) || 0;
  return `${n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${devise}`;
}

/** Demandeur : voir toutes les propositions de prix et retenir la bonne. */
export default function PropositionsModal({
  besoin,
  propositions,
  onClose,
  onAccept,
  onAnnoncer,
  onCloturer,
  onUpdateBudget,
  contactHref = '/dashboard/messages',
}: Props) {
  const devise = besoin.monaie || 'MAD';
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [annonce, setAnnonce] = useState('');
  const [editBudget, setEditBudget] = useState(false);
  const [budgetText, setBudgetText] = useState(
    Number(besoin.budget) > 0 ? String(besoin.budget) : '',
  );
  const [budgetBusy, setBudgetBusy] = useState(false);
  const [budgetMsg, setBudgetMsg] = useState('');
  const attribue = besoin.statut === 'attribue';
  const gagnante = propositions.find((p) => p.statut === 'acceptee');

  const saveBudget = async () => {
    setBudgetBusy(true);
    setBudgetMsg('');
    setError('');
    try {
      const value = budgetText.trim() ? parseMoneyInput(budgetText) : null;
      await onUpdateBudget(value && value > 0 ? value : null, devise);
      setBudgetMsg('Nouveau prix enregistré. Les professionnels qui ont postulé ont été prévenus.');
      setEditBudget(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de modifier le prix.');
    } finally {
      setBudgetBusy(false);
    }
  };

  const accept = async (proposition: BesoinProposition) => {
    setBusyId(proposition.id);
    setError('');
    try {
      await onAccept(proposition);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de retenir cette proposition.');
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-background-50 rounded-xl border border-background-200/70">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200/70">
          <div className="min-w-0">
            <h3 className="text-base font-bold font-heading text-foreground-950 truncate">{besoin.titre}</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              {propositions.length} proposition{propositions.length > 1 ? 's' : ''}
              {besoin.ville ? ` · ${besoin.ville}` : ''}
              {Number(besoin.budget) > 0 ? ` · votre prix : ${fmt(besoin.budget, devise)}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {besoin.description && (
            <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">{besoin.description}</p>
          )}

          {annonce && (
            <div className="flex items-start gap-2 px-4 py-2.5 bg-accent-50 border border-accent-200 rounded-lg text-xs text-accent-900">
              <i className="ri-notification-3-line mt-0.5"></i>
              {annonce}
            </div>
          )}

          {/* Prix proposé : le demandeur peut le réviser après avoir vu les offres. */}
          {!attribue && (
            <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-3.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400">Votre prix proposé</p>
                  <p className="text-sm font-bold text-foreground-950">
                    {Number(besoin.budget) > 0 ? fmt(besoin.budget, devise) : 'À négocier'}
                  </p>
                </div>
                {!editBudget && (
                  <button
                    onClick={() => { setEditBudget(true); setBudgetMsg(''); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 border border-background-200/70 text-foreground-700 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-edit-line"></i>
                    Modifier mon prix
                  </button>
                )}
              </div>
              {editBudget && (
                <div className="mt-3 flex flex-col sm:flex-row sm:items-end gap-2">
                  <div className="flex-1 min-w-0">
                    <label className="block text-[11px] font-medium text-foreground-600 mb-1">Nouveau montant ({devise})</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={budgetText}
                      onChange={(e) => setBudgetText(e.target.value)}
                      placeholder="ex. 1500 (vide = à négocier)"
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setEditBudget(false); setBudgetText(Number(besoin.budget) > 0 ? String(besoin.budget) : ''); }}
                      className="px-3 py-2 rounded-full bg-background-50 border border-background-200/70 text-foreground-600 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={saveBudget}
                      disabled={budgetBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
                    >
                      {budgetBusy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                      Enregistrer
                    </button>
                  </div>
                </div>
              )}
              {budgetMsg && (
                <p className="mt-2 text-[11px] text-accent-800 bg-accent-50 border border-accent-200 rounded-md px-2.5 py-1.5 flex items-start gap-1.5">
                  <i className="ri-checkbox-circle-line mt-0.5"></i>
                  {budgetMsg}
                </p>
              )}
            </div>
          )}

          {attribue && gagnante && (
            <div className="flex items-start gap-3 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg">
              <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-trophy-line"></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-900">
                  Mission attribuée à {gagnante.auteur_nom || 'un professionnel'}
                </p>
                <p className="text-xs text-foreground-600 mt-0.5">
                  Prix retenu : {fmt(gagnante.prix, gagnante.monaie || devise)}. Contactez-le pour le lieu et les détails.
                </p>
                <Link
                  to={contactHref}
                  className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-semibold no-underline whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-chat-3-line"></i>
                  Écrire pour les détails
                </Link>
              </div>
            </div>
          )}

          {!attribue && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  setError('');
                  try {
                    await onAnnoncer();
                    setAnnonce(
                      besoin.ville
                        ? `Annonce envoyée aux professionnels de la zone « ${besoin.ville} ».`
                        : 'Annonce envoyée à tous les professionnels de la catégorie.',
                    );
                  } catch {
                    setError('L’annonce n’a pas pu être envoyée.');
                  }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors"
              >
                <i className="ri-megaphone-line"></i>
                {besoin.ville ? `Annoncer à ${besoin.ville}` : 'Annoncer (notification)'}
              </button>
              <button
                onClick={async () => {
                  await onCloturer();
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors"
              >
                <i className="ri-close-circle-line"></i>
                Clôturer le besoin
              </button>
            </div>
          )}

          {propositions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
                <i className="ri-inbox-line text-xl text-foreground-400"></i>
              </div>
              <p className="text-sm font-medium text-foreground-700">Aucune proposition pour l&apos;instant</p>
              <p className="text-xs text-foreground-500 mt-1 max-w-sm">
                Annoncez votre besoin pour notifier les professionnels concernés : leurs propositions apparaîtront ici.
              </p>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {propositions.map((p) => {
                const retenue = p.statut === 'acceptee';
                const refusee = p.statut === 'refusee';
                return (
                  <li
                    key={p.id}
                    className={`rounded-lg border p-3.5 ${retenue ? 'border-primary-300 bg-primary-50/60' : refusee ? 'border-background-200/70 bg-background-100/40 opacity-70' : 'border-background-200/70 bg-background-50'}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-foreground-900 truncate">{p.auteur_nom || 'Professionnel'}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${p.type === 'acceptation' ? 'bg-accent-100 text-accent-900' : 'bg-secondary-100 text-secondary-900'}`}>
                            {p.type === 'acceptation' ? 'A accepté votre prix' : 'Proposition de prix'}
                          </span>
                          {retenue && <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-[10px] font-semibold whitespace-nowrap">Retenue</span>}
                        </div>
                        {p.message && <p className="text-xs text-foreground-600 mt-1.5 whitespace-pre-line">{p.message}</p>}
                        <p className="text-[11px] text-foreground-400 mt-1">
                          {new Date(p.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-bold text-foreground-950 whitespace-nowrap">{fmt(p.prix, p.monaie || devise)}</div>
                        {!attribue && (
                          <button
                            onClick={() => accept(p)}
                            disabled={busyId === p.id}
                            className="mt-1.5 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
                          >
                            {busyId === p.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                            Retenir
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-1.5">
              <i className="ri-error-warning-line mt-0.5"></i>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}